/**
 * "Ask about this" — the assistant offered where the question arises.
 *
 * Discoverability is not really solved by making one header button louder. It
 * is solved by the page offering the assistant at the moment a figure raises a
 * question, with the question already phrased. That converts the assistant from
 * something the user has to remember into something the work hands them.
 *
 * Renders nothing when no assistant is mounted, so a deployment with the
 * feature flag off never shows a control that would do nothing.
 */
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

import { useAskAssistant } from "@/features/chat/intent";

interface AskAboutButtonProps {
  /** The question to hand over, already phrased for this panel's figures. */
  question: string;
  className?: string;
}

export function AskAboutButton({ question, className = "" }: AskAboutButtonProps) {
  const { t } = useTranslation();
  const assistant = useAskAssistant();
  if (!assistant.available) return null;

  return (
    <button
      type="button"
      onClick={() => assistant.ask(question)}
      // The question itself is the tooltip: the user should be able to see what
      // will be asked before committing a request from a metered budget.
      title={question}
      className={`inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-fg-subtle transition-colors hover:border-brand hover:text-brand ${className}`}
    >
      <Sparkles aria-hidden="true" className="h-3 w-3 shrink-0" />
      <span>{t("chat.askAboutThis")}</span>
    </button>
  );
}
